import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, matchPath, useLocation } from "react-router";
import {
  Building2,
  ChartColumn,
  MapPin,
  Phone,
  Plus,
  Radar,
  Search,
  Settings,
  Sparkles,
  Sun,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { getSupabaseClient } from "../../providers/supabase/supabase";
import { offerColor, useOffers } from "../useOffers";
import { OfferDot } from "../ui";
import { OfferForm } from "../pipeline/OfferForm";
import { usePendingConfirm } from "../reviews/GoogleConfirmPage";
import { LegalLinks } from "../privacy/LegalLinks";
import { useIsDueno } from "../useIsDueno";

// Oportunidades abiertas por producto (para el contador del menú)
const useOpenDeals = () =>
  useQuery({
    queryKey: ["funnel_by_offer"],
    queryFn: async () => {
      const { data, error } = await getSupabaseClient()
        .from("funnel_by_offer")
        .select("slug, stage, deals");
      if (error) throw error;
      return data as { slug: string; stage: string; deals: number }[];
    },
    staleTime: 60 * 1000,
  });

const Item = ({
  to,
  icon: Icon,
  label,
  count,
  active,
  onClick,
}: {
  to: string;
  icon?: React.ComponentType<{ className?: string }>;
  label: React.ReactNode;
  count?: React.ReactNode;
  active: boolean;
  onClick?: () => void;
}) => (
  <Link
    to={to}
    onClick={onClick}
    className={cn(
      "flex items-center gap-2.5 rounded-lg px-2.5 py-2 font-semibold text-sm",
      active ? "bg-accent text-accent-foreground" : "hover:bg-secondary",
    )}
  >
    {Icon && <Icon className="size-[18px] shrink-0" />}
    <span className="truncate">{label}</span>
    {count != null && (
      <span className="ml-auto text-xs font-semibold text-[var(--faint)]">
        {count}
      </span>
    )}
  </Link>
);

const Label = ({ children }: { children: React.ReactNode }) => (
  <div className="flex items-center justify-between px-2.5 pt-4 pb-1.5 text-[11px] font-bold uppercase tracking-wider text-[var(--faint)]">
    {children}
  </div>
);

/** Menú izquierdo (en celular vive dentro del menú deslizable). */
export const Sidebar = ({ onNavigate }: { onNavigate?: () => void }) => {
  const { pathname } = useLocation();
  const [newOffer, setNewOffer] = useState(false);
  const { data: offers } = useOffers();
  const { data: funnel } = useOpenDeals();
  const isDueno = useIsDueno();
  // Radar and Google listings are owner-only (the freelancer role cannot read them)
  const { data: pending } = usePendingConfirm(isDueno);
  const is = (p: string) => !!matchPath(p, pathname);

  return (
    <nav
      aria-label="Menú principal"
      className="flex h-full flex-col gap-0.5 overflow-y-auto px-2.5 py-3.5"
    >
      <Link
        to="/"
        onClick={onNavigate}
        className="flex items-center gap-2.5 px-2 pb-3.5 text-base font-extrabold tracking-tight whitespace-nowrap"
      >
        <span className="grid size-7 place-items-center rounded-lg bg-primary text-sm font-extrabold text-primary-foreground">
          R
        </span>
        Raftel CRM
      </Link>
      <Item
        to="/"
        icon={Sun}
        label="Mi día"
        active={is("/") || is("/mi-dia")}
        onClick={onNavigate}
      />
      <Item
        to="/directory"
        icon={Search}
        label="Directorio"
        count="2.5 M"
        active={is("/directory/*")}
        onClick={onNavigate}
      />
      <Item
        to="/companies"
        icon={Building2}
        label="Empresas"
        active={is("/companies/*") || is("/contacts/*")}
        onClick={onNavigate}
      />
      <Item
        to="/tablero"
        icon={ChartColumn}
        label="Tablero"
        active={is("/tablero")}
        onClick={onNavigate}
      />
      {isDueno && (
        <>
          <Item
            to="/radar"
            icon={Radar}
            label="Radar de producto"
            active={is("/radar")}
            onClick={onNavigate}
          />
          <Item
            to="/fichas-google"
            icon={MapPin}
            label="Fichas de Google"
            count={pending ? pending.length : undefined}
            active={is("/fichas-google")}
            onClick={onNavigate}
          />
        </>
      )}
      <Item
        to="/investigaciones"
        icon={Sparkles}
        label="Investigaciones"
        active={is("/investigaciones")}
        onClick={onNavigate}
      />

      <Label>
        Oportunidades
        {/* the offer catalog is read-only for freelancers (E6.4) */}
        {isDueno && (
          <button
            onClick={() => setNewOffer(true)}
            title="Nuevo producto o servicio"
            aria-label="Nuevo producto o servicio"
            className="grid size-6 place-items-center rounded-md border hover:bg-secondary"
          >
            <Plus className="size-3.5" />
          </button>
        )}
      </Label>
      {offers?.map((o) => {
        const open =
          funnel
            ?.filter(
              (f) =>
                f.slug === o.slug &&
                f.stage !== o.won_stage &&
                f.stage !== o.lost_stage,
            )
            .reduce((s, f) => s + f.deals, 0) ?? 0;
        return (
          <Item
            key={o.id}
            to={`/board/${o.id}`}
            label={
              <span className="flex items-center gap-2.5">
                <OfferDot color={offerColor(o)} />
                {o.name}
              </span>
            }
            count={open}
            active={is(`/board/${o.id}`)}
            onClick={onNavigate}
          />
        );
      })}
      <Item
        to="/sesion"
        icon={Phone}
        label="Sesión de llamadas"
        active={is("/sesion")}
        onClick={onNavigate}
      />

      <Label>Ajustes</Label>
      <Item
        to="/ajustes"
        icon={Settings}
        label="IA y ajustes"
        active={is("/ajustes")}
        onClick={onNavigate}
      />
      <LegalLinks className="mt-auto pt-4" onNavigate={onNavigate} />
      <OfferForm
        target={newOffer ? { mode: "create" } : null}
        onClose={() => {
          setNewOffer(false);
          onNavigate?.();
        }}
      />
    </nav>
  );
};
